import React, { useState, useEffect, useCallback } from 'react';
import { getTasks, getTeams, getBrands, deleteTask } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { MagnifyingGlass, Trash, Eye, Plus, Clock, Warning, CaretUp, CaretDown } from '@phosphor-icons/react';

const STATUS_COLORS = {
  'New Idea Repository': '#C4B5FD', 'Backlog': '#E8E8E8', 'In Discussion': '#FFE7B3', 'In Design': '#D8B4E2',
  'In Development': '#B3D4FF', 'In QA': '#FFC2B3', 'Blocked': '#FF3B30',
  'Completed': '#34C759', 'Implemented': '#10B981',
};
const PRIORITY_COLORS = { High: '#FF3B30', Medium: '#FFB800', Low: '#34C759' };

export default function TablePage() {
  const [tasks, setTasks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [sortField, setSortField] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterTeam) params.assigned_team = filterTeam;
      if (filterBrand) params.brand_id = filterBrand;
      if (filterPriority) params.priority = filterPriority;
      if (search) params.search = search;
      const [tasksRes, teamsRes, brandsRes] = await Promise.all([getTasks(params), getTeams(), getBrands()]);
      setTasks(tasksRes.data);
      setTeams(teamsRes.data);
      setBrands(brandsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterStatus, filterTeam, filterBrand, filterPriority, search]);

  useEffect(() => {
    const timer = setTimeout(() => loadData(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadData, search]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this task?')) return;
    await deleteTask(id);
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...tasks].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const teamMap = Object.fromEntries(teams.map(t => [t.id, t.name]));
  const brandMap = Object.fromEntries(brands.map(b => [b.id, b.name]));

  const isOverdue = (task) => {
    if (!task.deadline || ['Completed', 'Implemented'].includes(task.status)) return false;
    return new Date(task.deadline) < new Date();
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <CaretUp size={12} /> : <CaretDown size={12} />;
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="table-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-['Manrope'] text-xl font-bold tracking-tight text-[#0F0F0F]">All Tasks</h1>
        <button
          data-testid="table-create-task-btn"
          onClick={() => navigate('/tasks/new')}
          className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] transition-colors flex items-center gap-2"
        >
          <Plus size={14} weight="bold" />
          New Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
          <Input
            data-testid="table-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tasks..."
            className="pl-8 h-8 text-sm border-[#E8E8E8] rounded-md"
          />
        </div>
        <select data-testid="table-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="table-filter-priority" value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Priorities</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
        <select data-testid="table-filter-team" value={filterTeam} onChange={e => setFilterTeam(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select data-testid="table-filter-brand" value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Brands</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-[#555555] text-sm py-8" data-testid="table-loading">Loading tasks...</div>
      ) : (
        <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tasks-table">
              <thead>
                <tr className="bg-[#F4F4F4]">
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555] cursor-pointer select-none" onClick={() => toggleSort('title')}>
                    <span className="flex items-center gap-1">Title <SortIcon field="title" /></span>
                  </th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Status</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555] cursor-pointer select-none" onClick={() => toggleSort('priority')}>
                    <span className="flex items-center gap-1">Priority <SortIcon field="priority" /></span>
                  </th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Type</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Team</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Brand</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Owner</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555] cursor-pointer select-none" onClick={() => toggleSort('deadline')}>
                    <span className="flex items-center gap-1">Deadline <SortIcon field="deadline" /></span>
                  </th>
                  <th className="px-3 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(task => (
                  <tr
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="border-t border-[#E8E8E8] hover:bg-[#F4F4F4] cursor-pointer transition-colors"
                    data-testid={`table-task-row-${task.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isOverdue(task) && <Warning size={14} className="text-[#FF3B30] shrink-0" />}
                        <span className="font-medium text-[#0F0F0F] truncate max-w-[250px]">{task.title}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: STATUS_COLORS[task.status] + '40', color: task.status === 'Blocked' ? '#FF3B30' : '#1A1A1A' }}>
                        {task.status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: PRIORITY_COLORS[task.priority] + '18', color: PRIORITY_COLORS[task.priority] }}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{task.task_type}</td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{teamMap[task.assigned_team] || '-'}</td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{brandMap[task.brand_id] || '-'}</td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{task.current_owner_name || '-'}</td>
                    <td className="px-3 py-3">
                      {task.deadline ? (
                        <div className="flex items-center gap-1">
                          <Clock size={12} className={isOverdue(task) ? 'text-[#FF3B30]' : 'text-[#555555]'} />
                          <span className={`text-xs ${isOverdue(task) ? 'text-[#FF3B30] font-semibold' : 'text-[#555555]'}`}>
                            {new Date(task.deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </span>
                        </div>
                      ) : <span className="text-xs text-[#E8E8E8]">-</span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${task.id}`); }} className="p-1 hover:bg-[#E8E8E8] rounded-md" data-testid={`view-task-${task.id}`}>
                          <Eye size={14} className="text-[#555555]" />
                        </button>
                        <button onClick={(e) => handleDelete(task.id, e)} className="p-1 hover:bg-red-50 rounded-md" data-testid={`delete-task-${task.id}`}>
                          <Trash size={14} className="text-[#555555] hover:text-[#FF3B30]" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-sm text-[#555555]">
                      No tasks found. Create your first task to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
